import React, { useEffect, useState } from 'react';
import { ChefHat, Mail, Zap, Leaf, Crown, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

const BrineBaronLanding = () => {
  const [isVisible, setIsVisible] = useState({});
  const [baronCTAVisible, setBaronCTAVisible] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [selectedOlive, setSelectedOlive] = useState(null);
  const [stickyButtonStyle, setStickyButtonStyle] = useState('green');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
            
            // Special handling for CTA Baron
            if (entry.target.id === 'wholesale') {
              setBaronCTAVisible(true);
            }
          } else {
            // Hide Baron when scrolling away from CTA section
            if (entry.target.id === 'wholesale') {
              setBaronCTAVisible(false);
            }
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    // Handle sticky button color change based on scroll position
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Get section elements to detect background types
      const aboutSection = document.getElementById('about');
      const rangeSection = document.getElementById('range');
      const recipesSection = document.getElementById('recipes');
      
      if (!aboutSection || !rangeSection || !recipesSection) return;
      
      const aboutTop = aboutSection.offsetTop;
      const aboutBottom = aboutTop + aboutSection.offsetHeight;
      const rangeTop = rangeSection.offsetTop;
      const rangeBottom = rangeTop + rangeSection.offsetHeight;
      const recipesTop = recipesSection.offsetTop;
      const recipesBottom = recipesTop + recipesSection.offsetHeight;
      
      const currentScroll = scrollY + windowHeight / 2;
      
      // Determine button style based on current section background
      if (currentScroll >= aboutTop && currentScroll <= aboutBottom) {
        // About section (dark green background) - use cream button
        setStickyButtonStyle('cream');
      } else if (currentScroll >= recipesTop && currentScroll <= recipesBottom) {
        // Recipes section (green gradient background) - use cream button
        setStickyButtonStyle('cream');
      } else if (currentScroll > documentHeight - windowHeight * 1.2) {
        // CTA/Footer sections (photo backgrounds) - use cream button
        setStickyButtonStyle('cream');
      } else {
        // Range section and other light backgrounds - use green button
        setStickyButtonStyle('green');
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const oliveVarieties = [
    {
      id: 'green',
      name: 'The Green',
      emoji: '🟢',
      description: 'Crisp, zesty and full of life — our classic green olive kicks off any platter with confidence.',
      color: 'from-emerald-500 to-green-600',
      image: 'https://customer-assets.emergentagent.com/job_brinebarons/artifacts/vupa2omp_ChatGPT%20Image%20Sep%2020%2C%202025%20at%2001_50_35%20PM.png',
      detailedDescription: {
        origin: 'Sourced from premium South Australian groves, our green olives are picked at the perfect stage of ripeness.',
        taste: 'Bright, briny flavor with a satisfying crunch. Clean finish with subtle grassy notes that complement any dish.',
        uses: 'Perfect for antipasto platters, Mediterranean salads, pizza toppings, or straight from the jar.',
        process: 'Traditional Spanish-style brining process using sea salt and natural fermentation for authentic flavor.',
        pairings: 'Excellent with aged cheeses, crusty bread, charcuterie, or mixed into pasta dishes.',
        storage: 'Store refrigerated after opening. Best served at room temperature for optimal flavor.'
      }
    },
    {
      id: 'kalamata',
      name: 'The Kalamata',
      emoji: '🟣',
      description: 'Deep purple, rich flavour, a Mediterranean legend.',
      color: 'from-purple-600 to-purple-800',
      image: 'https://customer-assets.emergentagent.com/job_brine-baron/artifacts/st3scwp9_ChatGPT%20Image%20Sep%2020%2C%202025%20at%2001_45_49%20PM.png',
      detailedDescription: {
        origin: 'Authentic Kalamata-style olives grown in South Australia, maintaining the traditional Mediterranean character.',
        taste: 'Rich, wine-like flavor with a meaty texture. Complex taste profile with hints of fruit and a pleasant saltiness.',
        uses: 'Essential for Greek salads, tapenade, Mediterranean cooking, or enjoyed on their own as a sophisticated snack.',
        process: 'Cured in red wine vinegar and sea salt, following time-honored Greek methods for authentic flavor development.',
        pairings: 'Perfect with feta cheese, tomatoes, oregano, olive oil, and crusty sourdough bread.',
        storage: 'Keep refrigerated in brine. Allow to come to room temperature before serving for best taste.'
      }
    },
    {
      id: 'mixed',
      name: 'The Mixed',
      emoji: '🫒',
      description: 'Green, black, pitted, wild — this one is for the indecisive and the bold alike.',
      color: 'from-amber-600 to-orange-700',
      image: 'https://customer-assets.emergentagent.com/job_brinebarons/artifacts/jk6cv0f4_ChatGPT%20Image%20Sep%2020%2C%202025%20at%2002_16_47%20PM.png',
      detailedDescription: {
        origin: 'A carefully curated blend of our finest olives from different South Australian groves.',
        taste: 'Complex flavor journey with each olive offering its own character - from bright and crisp to rich and meaty.',
        uses: 'Ideal for entertaining, mixed platters, or when you want variety in every bite. Great for cooking too.',
        process: 'Each variety is cured separately using its optimal method, then combined for the perfect flavor balance.',
        pairings: 'Versatile enough for any occasion - pairs well with anything from casual snacks to gourmet presentations.',
        storage: 'Store in refrigerator. Mix gently before serving to ensure even distribution of varieties.'
      }
    },
    {
      id: 'pitted-green',
      name: 'The Pitted Green',
      emoji: '🟢',
      description: 'Same bold green flavour. Perfect for quick tossing in salads or pasta.',
      color: 'from-lime-500 to-green-500',
      image: 'https://customer-assets.emergentagent.com/job_brinebarons/artifacts/nnwigeuo_IMG_0571.png',
      detailedDescription: {
        origin: 'Our premium green olives, carefully pitted while maintaining their firm texture and full flavor.',
        taste: 'All the crisp, zesty flavor of our classic green olives, ready to use without the pit.',
        uses: 'Perfect for salads, pasta dishes, pizza, cooking, or anywhere you need olive flavor without the hassle.',
        process: 'Expertly pitted using precision techniques that preserve the olive\'s shape and prevent mushiness.',
        pairings: 'Excellent in warm dishes, salads, sandwiches, or blended into sauces and dressings.',
        storage: 'Refrigerate after opening. Great for meal prep as they\'re ready to use straight from the container.'
      }
    },
    {
      id: 'pitted-kalamata',
      name: 'The Pitted Kalamata',
      emoji: '🟣',
      description: 'The classic, but without the pip. Ready to devour, marinate, or blend into a tapenade. May contain occasional pip.',
      color: 'from-violet-600 to-purple-700',
      image: 'https://customer-assets.emergentagent.com/job_brinebarons/artifacts/g4ykjp0a_IMG_0570.png',
      detailedDescription: {
        origin: 'Premium Kalamata-style olives, expertly pitted while preserving their distinctive flavor and texture.',
        taste: 'Full-bodied, wine-like flavor with the convenience of no pip. Rich and complex taste in every bite. May contain occasional pip.',
        uses: 'Ideal for tapenade, pasta sauces, Mediterranean cooking, or eating straight from the container.',
        process: 'Carefully pitted to maintain the olive\'s integrity, then kept in traditional red wine vinegar brine. Despite careful pitting, occasional pip may remain.',
        pairings: 'Perfect for cooking with garlic, herbs, pasta, or blending into spreads and dips.',
        storage: 'Keep refrigerated in brine. Perfect for quick meal preparation and cooking applications.'
      }
    },
    {
      id: 'wild',
      name: 'The Wild',
      emoji: '🌿',
      description: 'Untamed. Rare. A punch of character for people who know their olives.',
      color: 'from-teal-600 to-emerald-700',
      image: 'https://customer-assets.emergentagent.com/job_brinebarons/artifacts/z7w7gy9u_ChatGPT%20Image%20Sep%2020%2C%202025%20at%2001_43_30%20PM.png',
      detailedDescription: {
        origin: 'Specialty olives from heritage groves, representing unique varieties and artisanal cultivation methods.',
        taste: 'Bold, distinctive flavors that can\'t be found elsewhere. Each batch offers a unique taste adventure.',
        uses: 'For the olive connoisseur who appreciates rare and unusual flavors. Perfect for special occasions.',
        process: 'Small-batch processing using traditional methods, with minimal intervention to preserve natural character.',
        pairings: 'Best enjoyed on their own to appreciate their unique qualities, or paired with artisanal cheeses.',
        storage: 'Handle with care. Store refrigerated and consume within recommended timeframe for peak flavor.'
      }
    }
  ];

  const recipes = [
    {
      title: 'Olive Tapenade Blitz',
      description: 'A bold spread that brings Mediterranean vibes to any gathering',
      icon: ChefHat,
      time: '15 mins',
      image: 'https://images.pexels.com/photos/14715957/pexels-photo-14715957.jpeg',
      recipe: {
        ingredients: [
          '1 cup Brine Barons Green Olives, pitted',
          '1/2 cup Brine Barons Kalamata Olives, pitted',
          '2 cloves garlic, minced',
          '2 tbsp capers, drained',
          '3 tbsp extra virgin olive oil',
          '1 tbsp lemon juice',
          '2 tbsp fresh parsley, chopped',
          '1 tsp fresh thyme',
          'Black pepper to taste'
        ],
        instructions: [
          'In a food processor, pulse the garlic until finely chopped.',
          'Add both varieties of Brine Barons olives and capers. Pulse until roughly chopped.',
          'With the processor running, slowly drizzle in olive oil and lemon juice.',
          'Pulse until you achieve your desired consistency - chunky or smooth.',
          'Transfer to a bowl and stir in fresh herbs and black pepper.',
          'Let sit for 30 minutes to allow flavours to meld.',
          'Serve with crusty bread, crackers, or as a pasta sauce.',
          'Store covered in refrigerator for up to 1 week.'
        ],
        tips: 'For extra depth, add a splash of brine from the olive jar!'
      }
    },
    {
      title: 'Brine-Soaked Focaccia',
      description: 'Rustic bread with that signature Brine Barons punch',
      icon: Leaf,
      time: '2 hours',
      image: 'https://images.unsplash.com/photo-1711805064484-a77096f599a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxmb2NhY2NpYSUyMGJyZWFkfGVufDB8fHx8MTc1NDExMzE3OXww&ixlib=rb-4.1.0&q=85',
      recipe: {
        ingredients: [
          '3 cups bread flour',
          '1 cup warm water',
          '1/4 cup Brine Barons olive brine',
          '2 tbsp olive oil',
          '1 packet active dry yeast',
          '1 tsp sugar',
          '1 tsp salt',
          '1 cup Brine Barons Mixed Olives, halved',
          'Fresh rosemary sprigs',
          'Coarse sea salt',
          '2 tbsp olive oil for drizzling'
        ],
        instructions: [
          'Dissolve yeast and sugar in warm water. Let foam for 5 minutes.',
          'In a large bowl, combine flour and salt. Make a well in center.',
          'Pour yeast mixture, olive brine, and olive oil into well.',
          'Mix until dough forms. Knead for 8-10 minutes until smooth.',
          'Place in oiled bowl, cover, and rise for 1 hour until doubled.',
          'Oil a 9x13 baking pan. Transfer dough and stretch to fit.',
          'Dimple surface with fingers. Press olives and rosemary into dough.',
          'Let rise 30 minutes. Preheat oven to 425°F.',
          'Drizzle with olive oil and sprinkle with coarse salt.',
          'Bake 25-30 minutes until golden. Cool before slicing.'
        ],
        tips: 'The olive brine adds incredible depth - don\'t skip it!'
      }
    },
    {
      title: 'The Ultimate Dirty Martini',
      description: 'For those who like their drinks as bold as their olives',
      icon: Sparkles,
      time: '5 mins',
      image: 'https://images.unsplash.com/photo-1541807243774-518bdde38627?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxkaXJ0eSUyMG1hcnRpbml8ZW58MHx8fHwxNzU0MTEzMTg2fDA&ixlib=rb-4.1.0&q=85',
      recipe: {
        ingredients: [
          '3 oz premium gin or vodka',
          '1/2 oz dry vermouth',
          '1-2 tbsp Brine Barons olive brine (to taste)',
          '3-4 Brine Barons Green or Kalamata Olives',
          'Ice',
          'Lemon twist (optional)'
        ],
        instructions: [
          'Fill a mixing glass with ice.',
          'Add gin (or vodka), vermouth, and olive brine.',
          'Stir vigorously for 30 seconds to chill and dilute.',
          'Strain into a chilled martini glass.',
          'Garnish with Brine Barons olives on a cocktail pick.',
          'Express lemon oils over drink if desired.',
          'Serve immediately while ice-cold.'
        ],
        tips: 'Start with less brine and adjust to taste. Brine Barons olives have complex flavor that really shines in cocktails!'
      }
    }
  ];

  const handleEnquire = () => {
    window.location.href = 'mailto:sales@billsfarmfoodservice.com.au?subject=Brine Barons Enquiry&body=Hi there! I am interested in learning more about your premium olive range.';
  };

  return (
    <div className="min-h-screen text-stone-900 overflow-x-hidden max-w-full">
      {/* Dynamic Sticky Enquiry Button */}
      <div className="fixed bottom-6 right-6 z-50 sticky-mobile md:bottom-6 md:right-6">
        <button 
          onClick={handleEnquire}
          className={`w-16 h-16 md:w-20 md:h-20 rounded-full shadow-2xl hover:scale-110 transition-all duration-300 hover:shadow-3xl border-4 ${
            stickyButtonStyle === 'cream' 
              ? 'bg-brine-cream border-brine-cream' 
              : 'bg-brine-light-green border-brine-dark-green'
          }`}
          aria-label="Contact us for enquiries"
        >
          <img 
            src="https://customer-assets.emergentagent.com/job_brine-baron/artifacts/09scvj6s_987BA2C4-299D-41D7-8FE2-0100F6108E4E.png" 
            alt="Contact Brine Barons" 
            className="w-full h-full object-cover rounded-full p-1"
          />
        </button>
      </div>

      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_brine-baron/artifacts/k66b00vi_ChatGPT%20Image%20Aug%202%2C%202025%2C%2004_34_56%20PM.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-black bg-opacity-30"></div>
        
        <div className="container mx-auto px-0 md:px-6 text-center relative z-10">
          {/* Brine Barons Logo */}
          <div className="relative text-center">
            <div className="logo-float">
              <img 
                src="https://customer-assets.emergentagent.com/job_brinebarons/artifacts/s0sdbq1d_BrineBaronsLogo.png" 
                alt="Brine Barons - Where Flavour Reigns" 
                className="w-full max-w-xs md:max-w-xl lg:max-w-3xl xl:max-w-5xl mx-auto object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Baron Mascot Section - Under Hero */}
      <section className="py-12 md:py-16 bg-brine-cream">
        <div className="container mx-auto px-4 md:px-6 text-center">
          {/* Large Baron Mascot with Animation */}
          <div className="mb-8 md:mb-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_brine-baron/artifacts/y0ai1zpg_B99D1168-F5A4-4070-BB41-93EC062DB909.png" 
              alt="Brine Barons Mascot - Australian Olive Company" 
              className="w-48 h-48 md:w-64 md:h-64 lg:w-80 lg:h-80 mx-auto object-contain baron-bounce"
            />
          </div>

          {/* Authentic Story Content */}
          <div className="max-w-4xl mx-auto">
            <div className="text-lg md:text-xl lg:text-2xl leading-relaxed text-center body-text" style={{color: '#333', lineHeight: '1.7'}}>
              <p className="mb-6">
                Grown under the golden sun of South Australia, our olives are as bold and honest as the land they come from. At <span className="font-bold" style={{color: 'var(--brine-dark-green)'}}>Brine Barons</span>, we work with passionate growers who know their groves like family — because they are.
              </p>
              <p className="mb-6">
                These olives are picked at their peak, naturally fermented, and packed with nothing but briny goodness. <span className="font-semibold" style={{color: 'var(--brine-light-green)'}}>No shortcuts. Just real, flavour-rich olives</span> made for chefs, home cooks, and anyone who wants to bring a little southern soul to the table.
              </p>
              <p className="text-xl md:text-2xl font-bold body-text" style={{color: 'var(--brine-pale-green)'}}>
                Available in convenient 10kg and 1.2kg wholesale buckets.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" data-animate className="py-20 bg-brine-dark">
        <div className="container mx-auto px-6">
          {/* About Section */}
          <div className={`text-center max-w-4xl mx-auto transition-all duration-1000 ${isVisible.about ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`}>
            <h2 className="text-5xl md:text-7xl font-black mb-8 text-shadow-bold bold-header" style={{color: 'var(--brine-cream)'}}>
              Born in Brine. Raised by Barons.
            </h2>
            <div className="text-xl md:text-2xl leading-relaxed space-y-6">
              <p style={{color: 'var(--brine-light-green)'}} className="text-shadow">
                Brine Barons isn't just another olive brand.
              </p>
              <p style={{color: 'var(--brine-cream)'}} className="text-shadow">
                We represent bold flavour, small-batch pride, and the unshakable belief that food should <span className="font-black text-shadow-lg" style={{color: 'var(--brine-light-green)'}}>slap</span>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Range Section */}
      <section id="range" data-animate className="py-20 bg-brine-cream">
        <div className="container mx-auto px-6">
          <div className={`text-center mb-16 transition-all duration-1000 ${isVisible.range ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`}>
            <h2 className="text-5xl md:text-7xl font-black mb-6 text-shadow-bold bold-header" style={{color: 'var(--brine-dark-green)'}}>
              Our Range
            </h2>
            <p className="text-2xl font-bold text-shadow body-text" style={{color: 'var(--brine-light-green)'}}>Six varieties. Infinite possibilities.</p>
          </div>

          <div className="grid gap-8 max-w-6xl mx-auto">
            {oliveVarieties.map((olive, index) => (
              <div 
                key={olive.id}
                className={`transition-all duration-1000 delay-${index * 100} ${isVisible.range ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'}`}
              >
                <Card className="overflow-hidden shadow-2xl hover:shadow-3xl transition-all duration-300 hover:-translate-y-4 card-hover cursor-pointer" 
                      style={{
                        backgroundColor: 'var(--brine-cream)',
                        border: `3px solid var(--brine-light-green)`
                      }}
                      onClick={() => setSelectedOlive(olive)}>
                  <CardContent className="p-8">
                    <div className="flex flex-col md:flex-row items-center gap-8">
                      {/* Olive Image */}
                      <div className="w-40 h-40 md:w-48 md:h-48 rounded-xl overflow-hidden shadow-xl flex-shrink-0 border-4" style={{borderColor: 'var(--brine-dark-green)'}}>
                        <img 
                          src={olive.image} 
                          alt={olive.name}
                          className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                        />
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 text-center md:text-left">
                        <h3 className="text-3xl font-black mb-4 text-shadow bold-header" style={{color: 'var(--brine-dark-green)'}}>{olive.name}</h3>
                        <p className="text-lg mb-6 leading-relaxed font-medium body-text" style={{color: '#333'}}>
                          {olive.description}
                        </p>
                        <div className="inline-block px-6 py-3 rounded-full font-bold text-lg shadow-lg" 
                             style={{
                               backgroundColor: 'var(--brine-light-green)', 
                               color: 'var(--brine-dark-green)'
                             }}>
                          Available in 10kg & 1.2kg buckets
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recipe Inspiration Section */}
      <section id="recipes" data-animate className="py-20 bg-brine-gradient-reverse">
        <div className="container mx-auto px-6">
          <div className={`text-center mb-16 transition-all duration-1000 ${isVisible.recipes ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`}>
            <h2 className="text-5xl md:text-7xl font-black mb-6 text-shadow-bold bold-header" style={{color: 'var(--brine-cream)'}}>
              Recipe Inspiration
            </h2>
            <p className="text-2xl font-bold text-shadow body-text" style={{color: 'var(--brine-cream)'}}>Turn your Brine Barons olives into culinary magic</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {recipes.map((recipe, index) => (
              <div 
                key={recipe.title}
                className={`transition-all duration-1000 delay-${index * 200} ${isVisible.recipes ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`}
              >
                <Card 
                  className="h-full shadow-2xl hover:shadow-3xl transition-all duration-300 hover:-translate-y-4 card-hover recipe-clickable cursor-pointer" 
                  style={{
                    backgroundColor: 'var(--brine-cream)',
                    border: `3px solid var(--brine-dark-green)`
                  }}
                  onClick={() => setSelectedRecipe(recipe)}
                >
                  <CardContent className="p-8 text-center h-full flex flex-col">
                    {/* Recipe Image */}
                    <div className="w-full h-48 mb-6 rounded-xl overflow-hidden shadow-lg" 
                         style={{border: `2px solid var(--brine-light-green)`}}>
                      <img 
                        src={recipe.image} 
                        alt={recipe.title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    
                    <h3 className="text-2xl font-black mb-4 text-shadow bold-header" style={{color: 'var(--brine-dark-green)'}}>{recipe.title}</h3>
                    <p className="mb-6 flex-grow leading-relaxed font-medium body-text" style={{color: '#333'}}>{recipe.description}</p>
                    <div className="flex justify-between items-center">
                      <div className="inline-block text-sm font-bold px-6 py-3 rounded-full shadow-lg" 
                           style={{
                             color: 'var(--brine-cream)', 
                             backgroundColor: 'var(--brine-light-green)'
                           }}>
                        <Zap className="w-4 h-4 inline mr-2" />
                        {recipe.time}
                      </div>
                      <div className="text-sm font-bold px-4 py-2 rounded-full border-2 hover:scale-105 transition-transform" 
                           style={{
                             color: 'var(--brine-dark-green)', 
                             borderColor: 'var(--brine-pale-green)',
                             backgroundColor: 'transparent'
                           }}>
                        Click for Recipe
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>

          {/* Recipe Modal */}
          {selectedRecipe && (
            <div className="recipe-modal" onClick={() => setSelectedRecipe(null)}>
              <div className="recipe-modal-content p-8 m-4" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-start mb-6">
                  <h2 className="text-3xl font-black bold-header" style={{color: 'var(--brine-dark-green)'}}>
                    {selectedRecipe.title}
                  </h2>
                  <button 
                    onClick={() => setSelectedRecipe(null)}
                    className="text-2xl font-bold p-2 rounded-full hover:bg-gray-100 transition-colors"
                    style={{color: 'var(--brine-dark-green)'}}
                  >
                    ×
                  </button>
                </div>

                <div className="w-full h-64 mb-6 rounded-xl overflow-hidden shadow-lg">
                  <img 
                    src={selectedRecipe.image} 
                    alt={selectedRecipe.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                  {/* Ingredients */}
                  <div>
                    <h3 className="text-2xl font-bold mb-4 bold-header" style={{color: 'var(--brine-light-green)'}}>
                      Ingredients
                    </h3>
                    <ul className="space-y-2">
                      {selectedRecipe.recipe.ingredients.map((ingredient, index) => (
                        <li key={index} className="flex items-start">
                          <span className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0" 
                                style={{backgroundColor: 'var(--brine-pale-green)'}}></span>
                          <span>{ingredient}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Instructions */}
                  <div>
                    <h3 className="text-2xl font-bold mb-4 bold-header" style={{color: 'var(--brine-light-green)'}}>
                      Instructions
                    </h3>
                    <ol className="space-y-3">
                      {selectedRecipe.recipe.instructions.map((instruction, index) => (
                        <li key={index} className="flex items-start">
                          <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0 text-white" 
                                style={{backgroundColor: 'var(--brine-dark-green)'}}>
                            {index + 1}
                          </span>
                          <span className="text-sm leading-relaxed">{instruction}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>

                {/* Tips */}
                <div className="mt-8 p-6 rounded-xl" style={{backgroundColor: 'var(--brine-pale-green)', color: 'var(--brine-dark-green)'}}>
                  <h4 className="text-lg font-bold mb-2 bold-header">💡 Chef's Tip</h4>
                  <p className="italic">{selectedRecipe.recipe.tips}</p>
                </div>
              </div>
            </div>
          )}

          {/* Olive Modal */}
          {selectedOlive && (
            <div className="recipe-modal" onClick={() => setSelectedOlive(null)}>
              <div className="recipe-modal-content p-8 m-4" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-start mb-6">
                  <h2 className="text-3xl font-black bold-header" style={{color: 'var(--brine-dark-green)'}}>
                    {selectedOlive.name}
                  </h2>
                  <button 
                    onClick={() => setSelectedOlive(null)}
                    className="text-2xl font-bold p-2 rounded-full hover:bg-gray-100 transition-colors"
                    style={{color: 'var(--brine-dark-green)'}}
                  >
                    ✕
                  </button>
                </div>

                <div className="w-full h-64 mb-6 rounded-xl overflow-hidden shadow-lg">
                  <img 
                    src={selectedOlive.image} 
                    alt={selectedOlive.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Origin */}
                  <div>
                    <h3 className="text-xl font-bold mb-3 bold-header" style={{color: 'var(--brine-light-green)'}}>
                      Origin
                    </h3>
                    <p className="text-sm leading-relaxed body-text mb-4">{selectedOlive.detailedDescription.origin}</p>
                  </div>

                  {/* Taste */}
                  <div>
                    <h3 className="text-xl font-bold mb-3 bold-header" style={{color: 'var(--brine-light-green)'}}>
                      Taste Profile
                    </h3>
                    <p className="text-sm leading-relaxed body-text mb-4">{selectedOlive.detailedDescription.taste}</p>
                  </div>

                  {/* Uses */}
                  <div>
                    <h3 className="text-xl font-bold mb-3 bold-header" style={{color: 'var(--brine-light-green)'}}>
                      Best Uses
                    </h3>
                    <p className="text-sm leading-relaxed body-text mb-4">{selectedOlive.detailedDescription.uses}</p>
                  </div>

                  {/* Process */}
                  <div>
                    <h3 className="text-xl font-bold mb-3 bold-header" style={{color: 'var(--brine-light-green)'}}>
                      Process
                    </h3>
                    <p className="text-sm leading-relaxed body-text mb-4">{selectedOlive.detailedDescription.process}</p>
                  </div>

                  {/* Pairings */}
                  <div>
                    <h3 className="text-xl font-bold mb-3 bold-header" style={{color: 'var(--brine-light-green)'}}>
                      Perfect Pairings
                    </h3>
                    <p className="text-sm leading-relaxed body-text mb-4">{selectedOlive.detailedDescription.pairings}</p>
                  </div>

                  {/* Storage */}
                  <div>
                    <h3 className="text-xl font-bold mb-3 bold-header" style={{color: 'var(--brine-light-green)'}}>
                      Storage Tips
                    </h3>
                    <p className="text-sm leading-relaxed body-text mb-4">{selectedOlive.detailedDescription.storage}</p>
                  </div>
                </div>

                {/* Available Sizes */}
                <div className="mt-8 p-6 rounded-xl text-center" style={{backgroundColor: 'var(--brine-pale-green)', color: 'var(--brine-dark-green)'}}>
                  <h4 className="text-lg font-bold mb-2 bold-header">Available Sizes</h4>
                  <p className="text-xl font-bold">10kg & 1.2kg Wholesale Buckets</p>
                  <p className="text-sm mt-2">Perfect for foodservice professionals who give a damn about flavour</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Wholesale/Enquiry Section */}
      <section 
        id="wholesale" 
        data-animate 
        className="py-20 relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_brine-baron/artifacts/k3f8gqpk_Olive%20Tree%20at%20Dusk%20in%20Australia.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Overlay for text readability */}
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        
        <div className="container mx-auto px-6 text-center relative z-10">
          <div className={`max-w-4xl mx-auto transition-all duration-1000 ${isVisible.wholesale ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`}>
            <h2 className="text-5xl md:text-7xl font-black mb-8 text-shadow-bold text-white bold-header">
              Ready to Add Some Bite?
            </h2>
            <p className="text-xl md:text-2xl mb-12 leading-relaxed text-shadow text-white opacity-95 body-text">
              Want to add some bite to your offering?<br />
              Drop us a line — we'll send you the briniest samples you've ever tasted.
            </p>

            <Button 
              onClick={handleEnquire}
              size="lg"
              className="text-2xl px-16 py-8 font-black rounded-full shadow-2xl transition-all duration-300 hover:scale-110 hover:shadow-3xl btn-ripple body-text"
              style={{
                backgroundColor: 'var(--brine-cream)',
                color: 'var(--brine-dark-green)',
                border: `4px solid var(--brine-pale-green)`
              }}
            >
              <Mail className="w-8 h-8 mr-4" />
              Enquire Now
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-brine-cream">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-6 md:mb-0">
              <h3 className="text-3xl font-black bold-header" style={{color: 'var(--brine-dark-green)'}}>Brine Barons</h3>
              <p className="text-lg font-semibold body-text" style={{color: 'var(--brine-pale-green)'}}>Premium olives from South Australia</p>
            </div>
            <div className="flex flex-col items-center md:items-end gap-4">
              <a 
                href="mailto:sales@billsfarmfoodservice.com.au"
                className="transition-colors duration-300 text-lg font-bold body-text hover:opacity-80"
                style={{color: 'var(--brine-dark-green)'}}
              >
                sales@billsfarmfoodservice.com.au
              </a>
              <div className="text-sm font-medium body-text" style={{color: 'var(--brine-pale-green)'}}>
                © 2025 Brine Barons. All rights reserved.
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BrineBaronLanding;